import type { Result, AppError } from '../../error/types';
import { ok, err } from '../../error/functions';
import { getEnvironmentValue } from './getEnvironmentValue';
import { ENVIRONMENT_VALUES } from '../constants/ENVIRONMENT_VALUES';
import { SCANNER_CONSTANTS } from '../constants/SCANNER_CONSTANTS';
import { PROJECT_DOMAIN } from '@shared/constants';
import { isProduction } from './isProduction';
import { isPreview } from './isPreview';

/**
 * @group Functions
 * 
 * @summary
 * <blockquote>Constructs the scanner API URL based on the current environment</blockquote>
 * 
 * @returns Result containing the scanner API URL or an AppError if construction fails
 * 
 * @remarks
 * URL construction logic (in priority order):
 * 1. Explicit SCANNER_API_URL environment variable (highest priority, optional override)
 * 2. Production: `https://scanner.<project-domain>`
 * 3. Preview: `https://pr-{VERCEL_GIT_PULL_REQUEST_ID}.scanner.<project-domain>`
 * 4. Development without SCANNER_API_URL: Returns error
 * 
 * Preview environments derive the PR number from Vercel's VERCEL_GIT_PULL_REQUEST_ID
 * system variable, which is automatically set for preview deployments.
 * 
 * @example
 * ```typescript
 * import { getScannerApiUrl } from '@shared/utils';
 * 
 * const result = getScannerApiUrl();
 * result.match({
 *   ok: (url) => console.log(`Scanner URL: ${url}`),
 *   err: (error) => console.error(`Failed to construct URL: ${error.errorMessage}`)
 * });
 * ```
 */
export function getScannerApiUrl(): Result<string, AppError> {
  const explicitUrlResult = getEnvironmentValue(ENVIRONMENT_VALUES.SCANNER.SCANNER_API_URL, false);
  if (explicitUrlResult.isOk && explicitUrlResult.value) {
    return ok(explicitUrlResult.value);
  }

  const productionResult = isProduction();
  if (productionResult.isOk && productionResult.value) {
    return ok(`https://${SCANNER_CONSTANTS.SERVICE_NAME}.${PROJECT_DOMAIN}`);
  }

  const previewResult = isPreview();
  if (previewResult.isOk && previewResult.value) {
    const commitRefResult = getEnvironmentValue(ENVIRONMENT_VALUES.VERCEL.VERCEL_GIT_COMMIT_REF, false);
    if (commitRefResult.isOk && commitRefResult.value === 'absa-staging') {
      return ok(`https://absa-staging.${SCANNER_CONSTANTS.SERVICE_NAME}.${PROJECT_DOMAIN}`);
    }

    const prIdResult = getEnvironmentValue(ENVIRONMENT_VALUES.VERCEL.VERCEL_GIT_PULL_REQUEST_ID, false);
    if (prIdResult.isOk && prIdResult.value) {
      return ok(`https://pr-${prIdResult.value}.${SCANNER_CONSTANTS.SERVICE_NAME}.${PROJECT_DOMAIN}`);
    }

    return err({
      errorCode: 'MISSING_SCANNER_URL',
      errorMessage: 'Preview environment requires VERCEL_GIT_PULL_REQUEST_ID or SCANNER_API_URL to be set.'
    });
  }

  return err({
    errorCode: 'DEVELOPMENT_ENVIRONMENT',
    errorMessage: 'Set SCANNER_API_URL to the scanner API origin (e.g. https://pr-N.scanner.phila.life or http://localhost:8000).'
  });
}
